import * as THREE from 'three';
import { MeshSurfaceSampler } from 'three/examples/jsm/math/MeshSurfaceSampler.js';
import { settings } from '@/config';
import { createSandTextures } from '@/materials/proceduralTextures';
import { createLogger } from '@/utils/logger';
import type { QualityTier, TickState } from '@/types';

/** One blade's fixed per-instance data (position + orientation + jitter), stored so the
 *  instance matrices can be recomputed cheaply when height/width sliders change without
 *  re-running the (expensive) surface sampler. */
interface Blade {
  pos: THREE.Vector3;
  rotY: number;
  tilt: number;
  hj: number; // height jitter multiplier
  wj: number; // width jitter multiplier
}

/**
 * One spatial cell of a patch — the unit of culling and density LOD.
 *
 * A field is diced into these so the frustum test has something to bite on: a single 84×84 unit
 * meadow mesh counts as "in view" the moment any corner of it is, whereas ~20 tiles let the
 * camera reject everything it isn't actually looking at. See `settings.grass.culling`.
 */
interface Tile {
  mesh: THREE.InstancedMesh;
  /** This tile's blades, in the sampler's original (random) surface order — see {@link drawCount}. */
  blades: Blade[];
  /** World-space bounds, padded for blade height + wind bend. */
  center: THREE.Vector3;
  radius: number;
  /** Instances currently drawn (density LOD result), so `updateCulling` can skip no-op writes. */
  count: number;
  /** Whether this tile is currently wearing the low-poly blade (see `culling.simplifyDistance`). */
  lowPoly: boolean;
}

interface Patch {
  tiles: Tile[];
  /** Every blade of the patch, tile order — kept for `refreshTransforms`/`rebuild`. */
  blades: Blade[];
  source: THREE.Mesh;
  /** Scene index (from the source mesh name) — selects the per-scene size override. */
  scene: number;
  /** The SCENE-GATE decision (`setActiveScene`), kept apart from the culler's decision: a tile is
   *  drawn only when its chapter is revealed AND the camera can see it. Without this split the
   *  two systems would clobber each other's `visible` writes every frame. */
  revealed: boolean;
}

/**
 * Animated instanced grass — a compact take on the pmndrs grass-shader demo
 * (https://github.com/pmndrs/examples/tree/main/demos/grass-shader).
 *
 * Rather than a hand-rolled `ShaderMaterial`, this extends a single shared
 * `MeshPhysicalMaterial` via `onBeforeCompile`, so it inherits three.js instancing,
 * scene lighting, fog, tone mapping AND the built-in physically-based SHEEN lobe for
 * free — the sheen is what gives real grass its shimmer against the environment light
 * (the "sheen effect … realistic more according to environment" the user asked for).
 * Two small injections are layered on: a per-blade wind sway in the vertex stage and a
 * base→tip colour gradient in the fragment stage.
 *
 * Blades are scattered onto the ACTUAL surface of each custom grass mesh via an
 * area-weighted `MeshSurfaceSampler`, so they follow the real (possibly non-rectangular)
 * footprint and sit at the correct height. The source mesh is hidden by `World`.
 */
export class Grass {
  readonly group = new THREE.Group();
  readonly material: THREE.MeshPhysicalMaterial;

  private readonly geometry: THREE.BufferGeometry;
  /** Reduced-segment blade for distant tiles — see `settings.grass.culling.simplifyDistance`. */
  private readonly geometryLow: THREE.BufferGeometry;
  private readonly patches: Patch[] = [];
  /** Running total of scattered blades — enforces `settings.grass.maxBladesTotal`. */
  private totalBlades = 0;
  /** Last scene index passed to {@link setActiveScene}, so a `rebuild()` can restore the gate.
   *  Defaults to "show everything" for the `loadAllAtOnce` path. */
  private activeScene = Number.POSITIVE_INFINITY;

  /** Blades actually scattered, total and per scene — what you ACTUALLY got after the
   *  `maxBladesPerPatch` / `maxBladesTotal` caps, which is often far below what `density`
   *  asked for on the big Scene 2 / Scene 4 fields. Surfaced in the debug GUI. */
  get bladeCount(): number {
    return this.totalBlades;
  }
  bladeCountByScene(): Record<number, number> {
    const out: Record<number, number> = {};
    for (const p of this.patches) out[p.scene] = (out[p.scene] ?? 0) + p.blades.length;
    return out;
  }

  /**
   * Scene-gated visibility, mirroring {@link World.setActiveScene} (2026-07-19, user: "that second
   * scene mesh grass is visible when i in first scene — i don't want to see future mesh").
   *
   * WHY THIS IS NEEDED SEPARATELY: the grass source planes are `permanentlyHidden`, so
   * `collectSceneGroups` deliberately skips them, and the instanced meshes this class generates
   * live in `group` — added STRAIGHT to the scene, outside `model.root`. That put the grass
   * entirely outside the scroll-section visibility system: while the architecture correctly hid
   * future scenes, their grass stayed visible on the horizon the whole time.
   *
   * `cumulative` (the shipping mode) shows this scene and every earlier one. Patches whose name
   * carried no `_S_n` suffix parse to scene 0 and so are always visible — a deliberate default,
   * since an unclassified patch belongs to no particular chapter.
   */
  setActiveScene(index: number): void {
    this.activeScene = index;
    for (const patch of this.patches) this.setRevealed(patch, patch.scene <= index);
  }

  /** Exclusive counterpart of {@link setActiveScene}, matching `World.showSceneExclusive`
   *  (scenes 3 and 4 are one combined scene). Scene-0 patches stay visible throughout. */
  showSceneExclusive(index: number): void {
    const visible = index >= 3 ? new Set([3, 4]) : new Set([index]);
    for (const patch of this.patches) {
      this.setRevealed(patch, patch.scene === 0 || visible.has(patch.scene));
    }
  }

  /** Apply a scene-gate decision to a patch. A hidden chapter's tiles go dark immediately; a
   *  revealed one hands control back to {@link updateCulling}, which decides per tile on the next
   *  evaluation (and is shown meanwhile, so a freshly revealed chapter is never blank for a
   *  frame). */
  private setRevealed(patch: Patch, revealed: boolean): void {
    patch.revealed = revealed;
    for (const tile of patch.tiles) tile.mesh.visible = revealed;
  }
  private groundMat: THREE.MeshStandardMaterial | null = null;
  private readonly log = createLogger('grass');
  private readonly _m = new THREE.Matrix4();
  private readonly _q = new THREE.Quaternion();
  private readonly _e = new THREE.Euler();
  private readonly _s = new THREE.Vector3();
  private readonly _camPos = new THREE.Vector3();
  /** Throttle for {@link updateCulling} (`settings.grass.culling.updateIntervalSec`). */
  private cullAccumulator = 0;
  /** Tracks the culling toggle so switching it OFF live restores full density exactly once,
   *  instead of every frame thereafter. */
  private cullingWasEnabled = true;
  /** Transient density factor while a water reflection pass is rendering — see
   *  {@link setReflectionActive}. 1 outside that pass. */
  private reflectionScale = 1;
  /** Quality-tier multiplier on every culling distance — see {@link setQualityTier}. */
  private distanceScale = 1;
  /** True while the blades wear the cheap Lambert material (low tier). */
  private cheapMode = false;

  private readonly uniforms = {
    uTime: { value: 0 },
    uWindStrength: { value: settings.grass.windStrength },
    uWindSpeed: { value: settings.grass.windSpeed },
    uWindDir: {
      value: new THREE.Vector2(settings.grass.windDir[0], settings.grass.windDir[1]),
    },
    uBaseColor: { value: new THREE.Color(settings.grass.baseColor) },
    uTipColor: { value: new THREE.Color(settings.grass.tipColor) },
  };
  /** Scene-2's OWN colour pair (2026-07-22) — its patches wear a second material that shares
   *  the wind/time uniforms above (sway stays in sync) but binds these colours instead. */
  private readonly scene2Colors = {
    uBaseColor: { value: new THREE.Color(settings.grass.scene2.baseColor) },
    uTipColor: { value: new THREE.Color(settings.grass.scene2.tipColor) },
  };
  private readonly scene2Material: THREE.MeshPhysicalMaterial;
  /** LOW-tier variants of the two materials above — see {@link buildCheapMaterial}. Built lazily
   *  on the first tier switch so a machine that never drops tier never compiles them. */
  private _cheapMaterial: THREE.MeshLambertMaterial | null = null;
  private _cheapScene2Material: THREE.MeshLambertMaterial | null = null;

  private get cheapMaterial(): THREE.MeshLambertMaterial {
    return (this._cheapMaterial ??= this.buildCheapMaterial());
  }

  private get cheapScene2Material(): THREE.MeshLambertMaterial {
    return (this._cheapScene2Material ??= this.buildCheapMaterial(this.scene2Colors));
  }

  constructor() {
    this.group.name = 'GrassField';
    this.geometry = buildBladeGeometry(5);
    this.geometryLow = buildBladeGeometry(2);
    this.material = this.buildMaterial();
    this.scene2Material = this.buildMaterial(this.scene2Colors);
  }

  /** Build the shared wind + gradient + sheen material. `colors` overrides the colour-uniform
   *  pair (scene 2); wind/time stay the SHARED objects so all fields sway together. */
  private buildMaterial(colors?: { uBaseColor: THREE.IUniform; uTipColor: THREE.IUniform }): THREE.MeshPhysicalMaterial {
    const cfg = settings.grass;
    const mat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      roughness: cfg.roughness,
      metalness: 0,
      side: THREE.DoubleSide,
      // Built-in physically-based sheen — the environment-lit shimmer.
      sheen: cfg.sheen,
      sheenColor: new THREE.Color(cfg.sheenColor),
      sheenRoughness: cfg.sheenRoughness,
    });
    mat.onBeforeCompile = (shader) => this.injectShader(shader, colors);
    return mat;
  }

  /**
   * The LOW-tier counterpart of {@link buildMaterial}: same wind sway and colour gradient, on a
   * plain `MeshLambertMaterial`. Dropping the physical model (and with it the sheen lobe,
   * roughness and the environment-map lookups) is the cheapest big win available on an
   * integrated GPU, where the blades' fragment cost dominates the frame. The injected chunks
   * (`common`, `begin_vertex`, `color_fragment`) exist in the Lambert shader too, so the same
   * injection works verbatim.
   */
  private buildCheapMaterial(colors?: {
    uBaseColor: THREE.IUniform;
    uTipColor: THREE.IUniform;
  }): THREE.MeshLambertMaterial {
    const mat = new THREE.MeshLambertMaterial({ color: 0xffffff, side: THREE.DoubleSide });
    mat.onBeforeCompile = (shader) => this.injectShader(shader, colors);
    return mat;
  }

  /** The shared wind-sway (vertex) + base→tip gradient (fragment) injection, applied to whichever
   *  base material the current quality tier calls for. */
  private injectShader(
    shader: { uniforms: Record<string, THREE.IUniform>; vertexShader: string; fragmentShader: string },
    colors?: { uBaseColor: THREE.IUniform; uTipColor: THREE.IUniform },
  ): void {
    {
      Object.assign(shader.uniforms, this.uniforms, colors ?? {});

      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          /* glsl */ `#include <common>
            uniform float uTime;
            uniform float uWindStrength;
            uniform float uWindSpeed;
            uniform vec2  uWindDir;
            varying float vBladeHeight;`,
        )
        .replace(
          '#include <begin_vertex>',
          /* glsl */ `#include <begin_vertex>
            // Blade geometry is a unit-tall strip (y in 0..1) → y IS the height percent.
            vBladeHeight = position.y;
            // Per-blade phase + a random speed offset from its world footprint
            // (instanceMatrix translation), so neighbours sway as a coherent wave but no
            // two blades move in perfect lockstep — reads as natural, not mechanical.
            vec3 iPos = vec3(instanceMatrix[3].x, instanceMatrix[3].y, instanceMatrix[3].z);
            float phase = iPos.x * 0.35 + iPos.z * 0.35;
            float rnd = fract(sin(iPos.x * 12.9898 + iPos.z * 78.233) * 43758.5453);
            float t = uTime * uWindSpeed * (0.8 + rnd * 0.4);
            // A slow large-scale GUST that rolls across the whole field (0..1), so the
            // meadow breathes in waves instead of a uniform shimmer.
            float gust = sin(uTime * uWindSpeed * 0.45 + iPos.x * 0.06 + iPos.z * 0.06) * 0.5 + 0.5;
            float sway = sin(t + phase) * 0.6 + sin(t * 1.7 + phase * 1.3) * 0.4;
            // Bend more toward the tip (quadratic) — the root stays planted. Applied in
            // the blade's LOCAL sway plane, then carried into world by instanceMatrix.
            float bend = sway * uWindStrength * (0.45 + gust) * vBladeHeight * vBladeHeight;
            transformed.x += bend * uWindDir.x;
            transformed.z += bend * uWindDir.y;`,
        );

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          /* glsl */ `#include <common>
            uniform vec3 uBaseColor;
            uniform vec3 uTipColor;
            varying float vBladeHeight;`,
        )
        .replace(
          '#include <color_fragment>',
          /* glsl */ `#include <color_fragment>
            diffuseColor.rgb *= mix(uBaseColor, uTipColor, clamp(vBladeHeight, 0.0, 1.0));`,
        );
    }
  }

  /** Scatter animated blades over one custom grass mesh (in world space). */
  addPatch(source: THREE.Mesh): void {
    source.updateWorldMatrix(true, false);

    let count = this.bladeCountFor(source);
    // Enforce the global blade budget — trim this patch to what's left, skip once spent.
    const remaining = settings.grass.maxBladesTotal - this.totalBlades;
    if (remaining <= 0) {
      this.log.warn(`Grass budget (${settings.grass.maxBladesTotal}) reached — skipping "${source.name}".`);
      return;
    }
    if (count > remaining) {
      this.log.warn(`Grass budget: trimming "${source.name}" ${count}→${remaining}.`);
      count = remaining;
    }
    if (count <= 0) return;
    this.totalBlades += count;

    const sampled = this.sampleBlades(source, count);
    const sceneIdx = this.sceneOf(source);
    // `materialFor` (not a direct pick) so a GUI re-scatter while the LOW tier is active keeps the
    // cheap material instead of quietly reinstating the expensive one.
    const material = this.materialFor(sceneIdx);

    // Dice the patch into a spatial grid. Blades stay in the SAMPLER'S ORDER inside each bucket,
    // which is what makes the density LOD work: `MeshSurfaceSampler` walks the surface in random
    // (area-weighted) order, so truncating a tile's `count` drops an unbiased scatter of its
    // blades rather than shaving off one edge of it.
    const cfg = settings.grass.culling;
    const tileSize = Math.max(cfg.tileSize, 1);
    const buckets = new Map<string, Blade[]>();
    for (const blade of sampled) {
      const key = `${Math.floor(blade.pos.x / tileSize)}|${Math.floor(blade.pos.z / tileSize)}`;
      let bucket = buckets.get(key);
      if (!bucket) buckets.set(key, (bucket = []));
      bucket.push(blade);
    }

    const patch: Patch = { tiles: [], blades: [], source, scene: sceneIdx, revealed: true };
    for (const [key, blades] of buckets) {
      const mesh = new THREE.InstancedMesh(this.geometry, material, blades.length);
      mesh.name = `Grass:${source.name}#${key}`;
      // Frustum culling back ON (it was globally disabled here, which is what made the grass
      // draw from every camera position). The padded sphere below is the honest fix for the
      // bending-blade overhang that the blanket disable was covering up.
      mesh.frustumCulled = true;
      mesh.castShadow = false;
      mesh.receiveShadow = false;

      const tile: Tile = {
        mesh,
        blades,
        center: new THREE.Vector3(),
        radius: 0,
        count: blades.length,
        lowPoly: false,
      };
      patch.tiles.push(tile);
      patch.blades.push(...blades);
      this.group.add(mesh);
    }

    this.applyMatrices(patch); // writes instance matrices AND seeds each tile's bounds
    this.patches.push(patch);
    this.log.info(
      `${sampled.length} blades on "${source.name}" across ${patch.tiles.length} culling tile(s).`,
    );
  }

  /** Recompute a tile's padded world-space bounding sphere from its blades, and publish it on the
   *  `InstancedMesh` so three.js frustum-culls against it.
   *
   *  Blade positions are already WORLD-space (`sampleBlades` bakes `source.matrixWorld` in) and
   *  `group` sits at the origin untransformed, so this sphere needs no further transform — which
   *  is also why `Frustum.intersectsObject` applying the identity `matrixWorld` to it is correct.
   *  The radius carries `boundsPadding` on top of the tallest blade so a tip bending in the wind
   *  can never poke outside the sphere and pop at the edge of frame. */
  private updateTileBounds(tile: Tile, patch: Patch): void {
    const cfg = settings.grass;
    const size = cfg.sizeByScene[patch.scene];
    const height = size?.height ?? cfg.bladeHeight;
    const box = new THREE.Box3();
    for (const blade of tile.blades) box.expandByPoint(blade.pos);
    box.getCenter(tile.center);
    // Blades grow UP from their sampled point, so the occupied volume extends above the footprint.
    tile.center.y += (height * (1 + cfg.heightJitter)) * 0.5;
    tile.radius =
      box.getSize(this._s).length() * 0.5 +
      height * (1 + cfg.heightJitter) * 0.5 +
      cfg.culling.boundsPadding;
    tile.mesh.boundingSphere = new THREE.Sphere(tile.center.clone(), tile.radius);
  }

  /** Give a grass source mesh a rough brown sand/dirt material and keep it VISIBLE, so the
   *  ground shows as real earth between the blades (2026-07-11, user request) instead of a
   *  hidden plane. Shared procedural material across every patch. */
  applyGround(source: THREE.Mesh): void {
    if (!this.groundMat) {
      const { map, bump } = createSandTextures(512);
      const rep = settings.grass.groundRepeat;
      for (const t of [map, bump]) {
        t.wrapS = t.wrapT = THREE.RepeatWrapping;
        t.repeat.set(rep, rep);
        t.anisotropy = 8;
      }
      map.colorSpace = THREE.SRGBColorSpace;
      this.groundMat = new THREE.MeshStandardMaterial({
        map,
        bumpMap: bump,
        bumpScale: 0.35,
        roughness: 1,
        metalness: 0,
      });
    }
    source.material = this.groundMat;
    source.visible = true;
    source.castShadow = false;
    source.receiveShadow = false;
  }

  /** Scene index (1-4) parsed from a grass source mesh's Blender name (`Grass_S_2`,
   *  `Grass_03_S_3`, `Stage_Grass_S_4`, …); 0 if none — selects the per-scene overrides. */
  private sceneOf(source: THREE.Mesh): number {
    const raw = (source.userData.rawName as string | undefined) ?? source.name;
    // `[._]` (not just `_`): a Blender duplicate suffix (`Grass_S_2.001`) would otherwise fail to
    // match and fall back to scene 0 — which, now that this drives visibility, means the patch
    // would show in EVERY scene. Same bug that leaked `Stage_S_4.001` into Scene 1.
    return Number(raw.match(/_S_?(\d)(?:[._]|$)/)?.[1] ?? 0);
  }

  private bladeCountFor(source: THREE.Mesh): number {
    const cfg = settings.grass;
    const box = new THREE.Box3().setFromObject(source);
    const size = box.getSize(new THREE.Vector3());
    const area = Math.max(size.x * size.z, 0.01);
    // Per-scene density is an ABSOLUTE override (2026-07-17 — it used to be a multiplier; see
    // `settings.grass.densityByScene`). No entry ⇒ the global `density`.
    const density = cfg.densityByScene[this.sceneOf(source)] ?? cfg.density;
    return Math.min(cfg.maxBladesPerPatch, Math.max(64, Math.round(area * density)));
  }

  private sampleBlades(source: THREE.Mesh, count: number): Blade[] {
    const sampler = new MeshSurfaceSampler(source).build();
    const local = new THREE.Vector3();
    const blades: Blade[] = [];
    for (let i = 0; i < count; i += 1) {
      sampler.sample(local);
      const pos = local.clone().applyMatrix4(source.matrixWorld);
      blades.push({
        pos,
        rotY: pseudoRandom(i * 12.9898) * Math.PI * 2,
        tilt: (pseudoRandom(i * 78.233) - 0.5) * 0.35,
        hj: 1 + (pseudoRandom(i * 3.17) - 0.5) * 2 * settings.grass.heightJitter,
        wj: 0.85 + pseudoRandom(i * 5.41) * 0.5,
      });
    }
    return blades;
  }

  /** (Re)compose every instance matrix from stored blade data + current cfg — cheap, no
   *  re-sampling, so height/width sliders update live. */
  private applyMatrices(patch: Patch): void {
    const cfg = settings.grass;
    // Per-scene size override (height/width), falling back to the global defaults.
    const size = cfg.sizeByScene[patch.scene];
    const height = size?.height ?? cfg.bladeHeight;
    const width = size?.width ?? cfg.bladeWidth;
    for (const tile of patch.tiles) {
      tile.blades.forEach((b, i) => {
        this._e.set(b.tilt, b.rotY, 0);
        this._q.setFromEuler(this._e);
        this._s.set(width * b.wj, height * b.hj, width * b.wj);
        this._m.compose(b.pos, this._q, this._s);
        tile.mesh.setMatrixAt(i, this._m);
      });
      tile.mesh.instanceMatrix.needsUpdate = true;
      // Bounds depend on the blade HEIGHT that was just applied, so they're re-seeded here —
      // which also keeps the height slider from silently invalidating every tile's sphere.
      this.updateTileBounds(tile, patch);
    }
  }

  /** Every grass material currently BUILT (the cheap low-tier pair only exists once a tier
   *  switch has created it). Handed to story mode's dark grade so the fields darken with the
   *  architecture instead of staying lit under it. */
  get gradeMaterials(): THREE.Material[] {
    const out: THREE.Material[] = [this.material, this.scene2Material];
    if (this._cheapMaterial) out.push(this._cheapMaterial);
    if (this._cheapScene2Material) out.push(this._cheapScene2Material);
    if (this.groundMat) out.push(this.groundMat);
    return out;
  }

  get bladeTotal(): number {
    return this.patches.reduce((n, p) => n + p.blades.length, 0);
  }

  /** Blades actually SUBMITTED this frame after culling + density LOD — the number that decides
   *  the frame cost, as opposed to `bladeTotal`'s authored count. Surfaced in the debug GUI. */
  get bladesDrawn(): number {
    let n = 0;
    for (const patch of this.patches) {
      if (!patch.revealed) continue;
      for (const tile of patch.tiles) if (tile.mesh.visible) n += tile.count;
    }
    return n;
  }

  /** Tiles currently submitted / tiles total — the culling hit rate, for the GUI readout. */
  get tilesDrawn(): [drawn: number, total: number] {
    let drawn = 0;
    let total = 0;
    for (const patch of this.patches) {
      total += patch.tiles.length;
      if (!patch.revealed) continue;
      for (const tile of patch.tiles) if (tile.mesh.visible) drawn += 1;
    }
    return [drawn, total];
  }

  update(tick: TickState, camera?: THREE.Camera): void {
    this.uniforms.uTime.value = tick.elapsed;
    if (camera) this.updateCulling(tick.delta, camera);
  }

  /**
   * Distance culling + density LOD, throttled to `culling.updateIntervalSec`.
   *
   * FRUSTUM culling is NOT done here — three.js already does it per draw, per pass, against the
   * padded sphere published in {@link updateTileBounds}, and doing it twice would just cost CPU.
   * What this adds is the part three can't know: how MANY of a tile's blades are worth drawing.
   *
   * Distance is measured to the tile's SURFACE (centre minus radius), not its centre, so a big
   * tile the camera is standing on top of stays at full density.
   */
  private updateCulling(delta: number, camera: THREE.Camera): void {
    const cfg = settings.grass.culling;
    if (!cfg.enabled) {
      // Switched off live from the GUI — hand every revealed tile back at full density.
      if (this.cullingWasEnabled) {
        for (const patch of this.patches) {
          for (const tile of patch.tiles) {
            tile.count = tile.blades.length;
            tile.mesh.count = tile.count;
            tile.mesh.visible = patch.revealed;
            tile.lowPoly = false;
            tile.mesh.geometry = this.geometry;
          }
        }
        this.cullingWasEnabled = false;
      }
      return;
    }
    this.cullingWasEnabled = true;

    this.cullAccumulator += delta;
    if (this.cullAccumulator < cfg.updateIntervalSec) return;
    this.cullAccumulator = 0;

    camera.getWorldPosition(this._camPos);
    // Every distance is scaled by the quality tier, so a weak device thins and drops tiles much
    // closer to the camera without needing a second set of tuned numbers.
    const ds = this.distanceScale;
    const near = cfg.lodNearDistance * ds;
    const span = Math.max(cfg.lodFarDistance * ds - near, 0.001);
    const floor = THREE.MathUtils.clamp(cfg.minDensity, 0, 1);
    const maxDistance = cfg.maxDistance * ds;
    const simplify = cfg.simplifyDistance * ds;

    for (const patch of this.patches) {
      if (!patch.revealed) continue;
      for (const tile of patch.tiles) {
        const distance = Math.max(this._camPos.distanceTo(tile.center) - tile.radius, 0);
        if (distance > maxDistance) {
          tile.mesh.visible = false;
          continue;
        }
        tile.mesh.visible = true;
        // Blade-shape LOD: distant tiles drop to the 2-segment blade (4 tris vs 10). Hysteresis of
        // one tile radius on the way BACK to full detail, so a tile straddling the threshold can't
        // flip geometry every evaluation.
        const lowPoly = tile.lowPoly ? distance > simplify - tile.radius : distance > simplify;
        if (lowPoly !== tile.lowPoly) {
          tile.lowPoly = lowPoly;
          tile.mesh.geometry = lowPoly ? this.geometryLow : this.geometry;
        }
        // Linear ramp full → floor across the near/far band.
        const t = THREE.MathUtils.clamp((distance - near) / span, 0, 1);
        const density = 1 - (1 - floor) * t;
        // `tile.count` is the MAIN-PASS count and stays free of `reflectionScale` — that factor is
        // transient (it's on only inside a reflection render) and folding it in here would compound
        // on itself every evaluation until the field emptied.
        const count = Math.max(1, Math.round(tile.blades.length * density));
        if (count !== tile.count) tile.count = count;
        tile.mesh.count = Math.max(1, Math.round(count * this.reflectionScale));
      }
    }
  }

  /**
   * Thin the grass for the duration of a water pool's REFLECTION pass (`WaterComplex` fires this
   * via `World`'s `onReflectionRender` hook).
   *
   * That nested pass re-renders the entire scene into a 128–1024px target — at full density it
   * was re-drawing all ~1.49M grass triangles for every reflecting pool, every frame, into a
   * buffer where a quarter of the blades looks identical. The scale is applied on the NEXT
   * culling evaluation for tiles that change, but written through immediately here so the very
   * next reflection draw already sees it.
   */
  setReflectionActive(active: boolean): void {
    const cfg = settings.grass.culling;
    const scale = active ? THREE.MathUtils.clamp(cfg.reflectionDensity, 0.01, 1) : 1;
    if (scale === this.reflectionScale) return;
    this.reflectionScale = scale;
    if (!cfg.enabled) return;
    for (const patch of this.patches) {
      if (!patch.revealed) continue;
      for (const tile of patch.tiles) {
        if (!tile.mesh.visible) continue;
        tile.mesh.count = Math.max(1, Math.round(tile.count * scale));
      }
    }
  }

  /**
   * Apply an adaptive quality tier: pull the culling distances in, and on the LOW tier drop the
   * blades onto a cheap material.
   *
   * The material swap is the important half. `MeshPhysicalMaterial` with `sheen` evaluates a
   * second BRDF lobe for every fragment, and grass is `DoubleSide` geometry covering large parts
   * of the screen — on an integrated GPU that fragment cost, not the vertex count, is what holds
   * the frame at 30fps. Lambert keeps the wind sway and the base→tip gradient (both are shader
   * injections that re-apply through `onBeforeCompile`) and only loses the shimmer.
   */
  setQualityTier(tier: QualityTier): void {
    const profile = settings.performance.profiles[tier];
    if (!profile) return;
    this.distanceScale = profile.grassDistanceScale;
    if (profile.cheapGrass !== this.cheapMode) {
      this.cheapMode = profile.cheapGrass;
      for (const patch of this.patches) {
        const material = this.materialFor(patch.scene);
        for (const tile of patch.tiles) tile.mesh.material = material;
      }
      this.log.info(`grass material → ${this.cheapMode ? 'cheap (Lambert, no sheen)' : 'physical (sheen)'}.`);
    }
    // Force the next evaluation rather than waiting out the throttle, so a tier change lands now.
    this.cullAccumulator = Number.POSITIVE_INFINITY;
  }

  /** The material a patch of the given scene should currently wear (scene 2 has its own colours;
   *  the low tier swaps the whole set for the cheap variants). */
  private materialFor(scene: number): THREE.Material {
    if (this.cheapMode) return scene === 2 ? this.cheapScene2Material : this.cheapMaterial;
    return scene === 2 ? this.scene2Material : this.material;
  }

  /** Live GUI: push colour / wind / sheen changes (no geometry rebuild). */
  applySettings(): void {
    const cfg = settings.grass;
    this.uniforms.uWindStrength.value = cfg.windStrength;
    this.uniforms.uWindSpeed.value = cfg.windSpeed;
    this.uniforms.uWindDir.value.set(cfg.windDir[0], cfg.windDir[1]);
    this.uniforms.uBaseColor.value.set(cfg.baseColor);
    this.scene2Colors.uBaseColor.value.set(cfg.scene2.baseColor);
    this.scene2Colors.uTipColor.value.set(cfg.scene2.tipColor);
    this.uniforms.uTipColor.value.set(cfg.tipColor);
    for (const material of [this.material, this.scene2Material]) {
      material.sheen = cfg.sheen;
      material.sheenColor.set(cfg.sheenColor);
      material.sheenRoughness = cfg.sheenRoughness;
      material.roughness = cfg.roughness;
    }
  }

  /** Live GUI: cheap re-layout for height/width jitter changes (keeps positions). */
  refreshTransforms(): void {
    for (const patch of this.patches) this.applyMatrices(patch);
  }

  /** Live GUI: full re-scatter (density / blade-count changes) — recreates each mesh. */
  rebuild(): void {
    const sources = this.patches.map((p) => p.source);
    this.releaseTiles();
    this.patches.length = 0;
    this.totalBlades = 0; // re-budget from scratch
    for (const source of sources) this.addPatch(source);
    // Fresh meshes default to visible — re-apply the scene gate or a rescatter from the debug GUI
    // would pop every future scene's grass back into view.
    this.setActiveScene(this.activeScene);
  }

  /** Detach + free every tile's `InstancedMesh` (the shared blade geometry and materials are
   *  owned by this class and are NOT freed here — `rebuild` reuses them). */
  private releaseTiles(): void {
    for (const patch of this.patches) {
      for (const tile of patch.tiles) {
        this.group.remove(tile.mesh);
        tile.mesh.dispose();
      }
      patch.tiles.length = 0;
    }
  }

  dispose(): void {
    this.releaseTiles();
    this.patches.length = 0;
    this.geometry.dispose();
    this.geometryLow.dispose();
    this.material.dispose();
    this.scene2Material.dispose();
    this._cheapMaterial?.dispose();
    this._cheapScene2Material?.dispose();
    if (this.groundMat) {
      this.groundMat.map?.dispose();
      this.groundMat.bumpMap?.dispose();
      this.groundMat.dispose();
    }
  }
}

/** A deterministic 0..1 hash (no Math.random — keeps scatter stable across rebuilds). */
function pseudoRandom(seed: number): number {
  const x = Math.sin(seed + 1) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * A unit-tall grass blade: a vertical strip in the local XY plane (base at y=0, tip at
 * y=1), tapering to a point, facing +Z. `segments` height divisions give it enough
 * vertices to curve smoothly under the wind bend. Width is unit (1) — the real width is
 * applied per-instance via the matrix scale, so it stays live-tunable. DoubleSide in the
 * material makes both faces visible.
 */
function buildBladeGeometry(segments: number): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i <= segments; i += 1) {
    const t = i / segments;
    const halfW = 0.5 * (1 - t * 0.9); // taper toward the tip
    positions.push(-halfW, t, 0, halfW, t, 0);
    normals.push(0, 0, 1, 0, 0, 1);
    uvs.push(0, t, 1, t);
  }
  for (let i = 0; i < segments; i += 1) {
    const a = i * 2;
    const b = i * 2 + 1;
    const c = i * 2 + 2;
    const d = i * 2 + 3;
    indices.push(a, c, b, c, d, b);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  return geo;
}
